import {createBrowserRouter, Navigate, RouteObject} from "react-router-dom";
import {useAuthStore} from "@/store/auth";
import DashboardLayout from "@/components/layout/DashboardLayout";
import DefaultLayout from "@/components/layout/DefaultLayout";
import SignIn from "@/pages/signin";
import SignUp from "@/pages/signup";
import SignupAdditionalDetails from "@/pages/signupAdditionalDetails";
import ForgotPassword from "@/pages/forgotPassword";
import ResetPassword from "@/pages/resetPassword";
import PrivacyPolicy from "@/pages/privacyPolicy";
import Dashboard from "@/pages/dashboard";
import DetailsPage from "@/pages/detailsPage";
import PublicConnectorGenerate from "@/pages/publicConnectorGenerate";
import PublicSourceConfiguration from "@/pages/publicSourceConfiguration";
import PublicDestinationConfiguration from "@/pages/publicDestinationConfiguration";
import PublicDataMapping from "@/pages/publicDataMapping";
import PublicIntegrationProfile from "@/pages/publicIntegrationProfile";
import PublicConfigsCards from "@/pages/publicConfigsCards";
import EditConfigurePublicConnector from "@/pages/editConfigurePublicConnector";
import EditPublicConnector from "@/pages/editPublicConnector";
import IntegrationSetup from "@/pages/integrationSetup";
import IntegrationEngine from "@/pages/integrationEngine";
import FAQ from "@/pages/faq";
import HelpCenter from "@/pages/helpCenter";
import ConnectorLibrary from "@/pages/connectorLibrary";
import ConnectorPage from "@/pages/connectorPage";
import MockDemoPage from "@/pages/mockdemo";
import Account from "@/pages/account";
import IntegrationStudio from "@/pages/integrationStudio/index";
import { AuthProvider } from "./contexts/AuthContext";
import { Outlet } from "react-router-dom";
import { Suspense, ReactNode } from 'react';
import { CircularProgress } from '@mui/material';
import { routes as customerRoutes } from './routes';
import { adminPageChildren, staffAdminRoutes, staffAuthPublicRoutes } from './staffAdminRoutes';
import AppAdminRedirect from '@/pages/admin/AppAdminRedirect';
import { getWebsiteSurface } from './adminHost';
import ImpersonationBanner from "@/components/ImpersonationBanner";

// Root layout that provides AuthProvider
const RootLayout = () => {
	return (
		<AuthProvider>
			<ImpersonationBanner />
			<Outlet />
		</AuthProvider>
	);
};

// Loading component
const LoadingFallback = () => (
	<div style={{
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		height: '100vh',
		width: '100vw'
	}}>
		<CircularProgress />
	</div>
);

interface ExtendedRouteObject extends Omit<RouteObject, 'children'> {
	children?: ExtendedRouteObject[];
	element?: ReactNode;
}

// Wrap all route elements with Suspense
const wrapRoutesWithSuspense = (routes: ExtendedRouteObject[]): ExtendedRouteObject[] => {
	return routes.map(route => ({
		...route,
		element: route.element ? <Suspense fallback={<LoadingFallback />}>{route.element}</Suspense> : undefined,
		...(route.children && { children: wrapRoutesWithSuspense(route.children) })
	}));
};

function withAppAdminRedirects(tree: ExtendedRouteObject[]): ExtendedRouteObject[] {
	return tree.map((route) => {
		if (!route.children) return route;
		return {
			...route,
			children: [
				{ path: '/admin', element: <AppAdminRedirect /> },
				{ path: '/admin/*', element: <AppAdminRedirect /> },
				...route.children,
			],
		};
	});
}

function withLocalAdmin(tree: ExtendedRouteObject[]): ExtendedRouteObject[] {
	return tree.map((route) => {
		if (!route.children) return route;
		return {
			...route,
			children: [
				...staffAuthPublicRoutes,
				...route.children.map((child) => {
					if (child.path === '/' && child.children) {
						return { ...child, children: [...child.children, ...adminPageChildren] };
					}
					return child;
				}),
			],
		};
	});
}

function selectRoutes(): ExtendedRouteObject[] {
	const surface = getWebsiteSurface();
	if (surface === 'staff') return staffAdminRoutes as ExtendedRouteObject[];
	if (surface === 'app') return withAppAdminRedirects(customerRoutes as ExtendedRouteObject[]);
	return withLocalAdmin(customerRoutes as ExtendedRouteObject[]);
}

const router = createBrowserRouter(wrapRoutesWithSuspense(selectRoutes()) as RouteObject[]);

export {router}; 